<template>
    <div v-if="from && until">
        <div 
            class="position-fixed top-0 end-0"
            style="z-index: 1050; right: 15px !important; top: 10px;"
            v-if="displayControls"
        >
            <div class="d-flex align-items-center gap-2">
                <div class="btn-group">
                <button 
                    class="btn"
                    :class="{
                    'btn-on': !isList,
                    'btn-off': isList
                    }"
                    @click="list = false"
                >
                    <b-icon icon="calendar"></b-icon> {{ $t('calendar') }}
                </button>
                <button 
                    class="btn"
                    :class="{
                    'btn-on': isList,
                    'btn-off': !isList
                    }"
                    @click="list = true"
                >
                    <b-icon icon="list"></b-icon> {{ $t('list') }}
                </button>
                </div>

                <button
                    class="btn ml-2 btn-outline-dark bg-white"
                    @click="scrollToWeek"
                >
                    {{ $t('go-to-current-week') }}
                </button>
            </div>
        </div>
        <div v-if="isList" class="container-fluid">
            <template v-for="(months, year) in listEvents">
                <div v-for="(weeks, month) in months" :key="`${year}-${month}`">
                    <h1>{{ $t(`month-long-${Number(month)+1}`) }} {{ year }}</h1>
                    <div class="list-group" v-for="(week, weekNo) in weeks" :key="`${year}-${month}-${weekNo}`">
                        <h2><b-badge>{{ week.weekCommencing }}</b-badge></h2>
                        <div class="list-group mb-2" :key="`${year}-${month}-${weekNo}`" :id="`${year}-${month}-${weekNo}`">
                            <div class="list-group-item" v-for="(day, date) in week.days" :key="`${date}`">
                                <strong> {{ getDayInfo(year,month,date) }} </strong>
                                <div class="timetable-period">
                                    <TimetableEvent
                                        v-for="event in day.events"
                                        :key="event.id"
                                        :event="event"
                                        :hideDetailsOnSmallScreen="true"
                                        @click="$emit('selectEvent', event)"
                                        class="timetable-event mb-2 p-3"
                                        :markAbsent="event.attended == 0"
                                    />
                                </div>
                            </div>                        
                        </div>
                    </div>
                </div>
            </template> 
        </div>
        <template v-else>
            <template v-for="(months, year) in daysByYearMonthWeek">
                <div v-for="(weeks, month) in months" :key="`${year}-${month}`">
                    <h2>{{ $t(`month-long-${Number(month)+1}`) }} {{ year }}</h2>
                    <table class="table table-bordered timetable-calendar">
                    <tbody>
                    <template v-for="(week, weekNo) in weeks">
                        <tr :key="`${year}-${month}-${weekNo}-1`" :id="`${year}-${month}-${weekNo}-1`">
                            <!-- Cannot colspan these; CSS calcs are based on rows of 7 elements -->
                            <th v-for="n in week.offsetStart" :key="n" class="hide" />
                            <th scope="col" class="timetable-calendar-heading"  v-for="(day, date) in week.days" :key="`${date}-1`">
                                {{ `${$t('dayname-short-' + (day.day === 0 ? 7 : day.day))}` }} {{ date }}
                            </th>
                            <th v-for="n in week.offsetEnd" :key="n" class="hide" />
                        </tr>

                        <tr :key="`${year}-${month}-${weekNo}-2`" class="mb-2">
                            <td v-for="n in week.offsetStart" :key="n" class="hide" />
                            <td v-for="(day, date) in week.days" :key="`${date}-2`" :class="[0, 6].includes(day.day) ? 'bg-secondary' : 'bg-white'">
                                <div class="timetable-period">
                                    <TimetableEvent
                                        v-for="event in day.events"
                                        :key="event.id"
                                        :event="event"
                                        :hideDetailsOnSmallScreen="true"
                                        @click="$emit('selectEvent', event)"
                                        class="timetable-event"
                                        :markAbsent="event.attended == 0"
                                    />
                                </div>
                            </td>
                            <td v-for="n in week.offsetEnd" :key="n" class="hide" />
                        </tr>
                        <tr :key="`${year}-${month}-${weekNo}-3`">
                            <td colspan="7" class="p-2 hide" />
                        </tr>
                    </template>
                    </tbody>
                    </table>
                </div>
            </template>
        </template>
        
    </div>
</template>

<script>
import EventContainer from 'src/modules/timetable/eventContainer'
import { dateFromISODate } from 'src/modules/timetable/util'

import TimetableEvent from './TimetableEvent.vue'

export default {
    components: {
        TimetableEvent,
    },
    props: {
        userId: {
            type: Number,
            required: true,
        },
        from: String,
        until: String,
        events: {
            type: Array,
            default: () => [],
        },
        bgEvents: {
            type: Array,
            default: () => [],
        },
        listView: {
            type: Boolean,
            default: false
        },
        displayControls: {
            type: Boolean,
            default: true
        }
    },
    mounted() {
        this.eventContainer = new EventContainer(this.events, this.bgEvents.map(event => {
            delete event.groups
            return event
        }))
        this.updateDays()
    },
    data() {
        return {
            eventContainer: null,
            daysByYearMonthWeek: {},
            listEvents: {},
            startDate: null,
            endDate: null,
            list: false
        }
    },
    methods: {
        /**
         * Generates the structure to generate the timetable from as a year -> month -> day -> details dictionary
         */
        updateDays() {
            if (!this.from || !this.until) return

            this.daysByYearMonthWeek = {}

            const start = dateFromISODate(this.from)
            const end = dateFromISODate(this.until)

            let prev = new Date(start)
            let week = this.getWeek(start) - (start.getDay() === 1 ? 1 : 0)

            let filteredEventsStructure = {};

            for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
                if (date.getMonth() !== prev.getMonth()) {
                    this.daysByYearMonthWeek[prev.getFullYear()][prev.getMonth()][week].offsetEnd = prev.getDay() ? 7 - prev.getDay() : undefined
                    week = 1
                } else if (date.getDay() === 1) week++

                if (!this.daysByYearMonthWeek[date.getFullYear()]) this.daysByYearMonthWeek[date.getFullYear()] = {}
                if (!this.daysByYearMonthWeek[date.getFullYear()][date.getMonth()]) this.daysByYearMonthWeek[date.getFullYear()][date.getMonth()] = {}
                if (!this.daysByYearMonthWeek[date.getFullYear()][date.getMonth()][week]) this.daysByYearMonthWeek[date.getFullYear()][date.getMonth()][week] = {
                    offsetStart: week !== 1 ? undefined : this.modulo(date.getDay() - 1, 7),
                    days: {},
                }
                
                // const events = this.eventContainer.getGroupEvents(date.getFullYear(), date.getMonth(), date.getDate(), -1);

                this.daysByYearMonthWeek[date.getFullYear()][date.getMonth()][week].days[date.getDate()] = {
                    day: date.getDay(),
                    events: this.eventContainer.getGroupEvents(date.getFullYear(), date.getMonth(), date.getDate(),  -1),
                }

                prev = new Date(date)

                // Filtered events
                Object.entries(this.daysByYearMonthWeek).forEach(([year, months]) => {
                Object.entries(months).forEach(([month, weeks]) => {
                    Object.entries(weeks).forEach(([week, weekData]) => {
                        Object.entries(weekData.days).forEach(([day, dayData]) => {
                            if (dayData.events && Object.keys(dayData.events).length > 0) {
                            filteredEventsStructure[year] ??= {};
                            filteredEventsStructure[year][month] ??= {};
                            filteredEventsStructure[year][month][week] ??= { days: {} };
                            filteredEventsStructure[year][month][week].days[day] = {
                                day: dayData.day,
                                events: dayData.events
                            };
                            }
                        });
                    });
                });
                });

            }

            this.listEvents = this.addWeekCommencing(filteredEventsStructure);

            this.daysByYearMonthWeek[prev.getFullYear()][prev.getMonth()][week].offsetEnd = prev.getDay() ? 7 - prev.getDay() : undefined
        },
        getWeek(date) {
            let monthStart = new Date(date);
            monthStart.setDate(0);
            let offset = (monthStart.getDay() + 1) % 7 - 1; // -1 is for a week starting on Monday
            return Math.ceil((date.getDate() + offset) / 7);
        },
        scrollToWeek() {
            const today = new Date()
            let key = undefined;
            if(this.list) key = `${today.getFullYear()}-${today.getMonth()}-${Math.ceil(today.getDate() / 7)}`;
            else key = `${today.getFullYear()}-${today.getMonth()}-${Math.ceil(today.getDate() / 7)}-1`;
            const week = document.getElementById(key)
            console.log(key)
            if (!week) return
            week.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest'
            })
    	},
        modulo(x, n) {
            return ((x % n) + n) % n
        },
        flattenDaysStructure() {
            const flattenedDays = [];

            for (const year in this.daysByYearMonthWeek) {
                const months = this.daysByYearMonthWeek[year];
                
                for (const month in months) {
                    const weeks = months[month];
                
                    for (const week in weeks) {
                        const days = weeks[week].days;

                        for (const day in days) {
                            const events = Object.keys(days[day].events).length ? days[day].events : null;

                            for(const event in events){
                                if(events[event] !== null) flattenedDays.push({
                                ...events[event] 
                            });
                            }
                        }
                    }
                }
            }
            
            flattenedDays.sort((a, b) => {
                if (a.year !== b.year) return a.year - b.year;
                if (a.month !== b.month) return a.month - b.month;
                if (a.week !== b.week) return a.week - b.week;
                return a.day - b.day;
            });
            
            return flattenedDays;
        },
        timestampToDate(timestamp){
            return new Date(timestamp * 1000);
        },
        addWeekCommencing(data) {
            const getMonday = (date) => {
                const d = new Date(date);
                const day = d.getDay(); // 0 (Sun) to 6 (Sat)
                const diff = d.getDate() - (day === 0 ? 6 : day - 1); // Adjust to Monday
                return new Date(d.setDate(diff));
            };

            for (const year in data) {
                for (const month in data[year]) {
                for (const weekNum in data[year][month]) {
                    const week = data[year][month][weekNum];
                    
                    const timestamps = [];
                    for (const day in week.days) {
                    for (const event of Object.values(week.days[day].events || {})) {
                        if (event.date) timestamps.push(event.date);
                    }
                    }

                    // Determine week commencing date
                    if (timestamps.length > 0) {
                        // Use earliest timestamp in the week
                        const earliestDate = new Date(Math.min(...timestamps));
                        const monday = getMonday(this.timestampToDate(earliestDate));
                        week.weekCommencing = `${this.$t('w-slash-c-on-colon')} ${monday.toLocaleDateString('en-GB', {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric'
                        })}`;
                    }
                }
                }
            }
            return data;
        },
        getDayInfo(year, month, day) {
            const date = new Date(year, month, day);

            if (isNaN(date.getTime())) {
                return "Invalid date";
            }

            const isoDayOfWeek = date.getDay() === 0 ? 7 : date.getDay(); 

            const getOrdinalSuffix = (day) => {
                const suffixes = ["th", "st", "nd", "rd"];
                const remainder = day % 100;
                return suffixes[(remainder - 20) % 10] || suffixes[remainder] || suffixes[0];
            };

            const dayWithSuffix = `${date.getDate()}${getOrdinalSuffix(date.getDate())}`;

            return `${this.$t(`dayname-long-${isoDayOfWeek}`)} ${dayWithSuffix}`;
        }
    },
    watch: {
        events: {
            deep: true,
            handler(newEvents) {
                if (!newEvents.length) return

                this.eventContainer = new EventContainer(newEvents, this.bgEvents)
            },
        },
        bgEvents: {
            deep: true,
            handler(newEvents) {
                if (!newEvents.length) return

                this.eventContainer = new EventContainer(this.events, newEvents)
            },
        },
        from() {
            this.updateDays()
        },
        until() {
            this.updateDays()
        }
    },
    computed: {
        flattenedDays(){
            return this.addWeekCommencing(this.flattenDaysStructure());
        },
        isList(){
            if(this.listView == true) return true;
            return this.list;
        }
    }
}
</script>

<style>
.current-week:hover {
    color: black;
}
</style>
